import React, { useContext, useEffect, useState, useRef } from "react";
import { Tabs as PolarisTabs } from "@shopify/polaris";

import { baseHelper, storageHelper } from "lib/helpers";
import constant from "lib/constant/constant";
import { PrivateContext } from "lib/context";
import { useNavigate, useLocation } from "react-router-dom";
import { StyledTabs } from "./tabsStyle";

import { adminTabs, operatorTabs, providerTabs } from "./tabsConfig/index";

const { userKey } = constant;
const { admin, operator, provider } = userKey;

const Tabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const { isLoading, vendorHiddenModule = [] } = useContext(PrivateContext);
  const { chatSetting = {}, cms, currentUser, unreadMessageCount } = useContext(PrivateContext);
  const { isCustomer, isVendor } = chatSetting || {};

  const isDisabled = !isCustomer && !isVendor;

  const getTabs = (role) => {
    if (role === admin) {
      return adminTabs(cms, isLoading);
    }

    if (role === operator) {
      let tabs = operatorTabs(cms, isLoading, currentUser);
      tabs = tabs.filter(
        (tab) =>
          (tab.id !== constant.QUOTES ||
            (tab.id === constant.QUOTES &&
              baseHelper.isPAASEnabled(currentUser._id) &&
              !currentUser.ecommercePlatform)) &&
          (tab.id !== constant.ANALYTICS ||
            (tab.id === constant.ANALYTICS && baseHelper.isRotaroUser(currentUser) && !currentUser.ecommercePlatform))
      );

      const updatedTabs = tabs
        .map((tab) => {
          if (tab.id === "chat") {
            tab.content = `${tab.content} (${unreadMessageCount})`;
            if (currentUser.ecommercePlatform || isDisabled) {
              return false;
            }
          }
          return tab;
        })
        .filter((tab) => tab);

      return updatedTabs;

    }

    if (role === provider) {
      let tabs = providerTabs(cms, isLoading, currentUser);
      tabs = tabs.filter(
        (tab) =>
          (tab.id !== constant.QUOTES ||
            (tab.id === constant.QUOTES &&
              baseHelper.isPAASEnabled(currentUser.introductorySeller) &&
              !currentUser.ecommercePlatform)) &&
          (tab.id !== constant.ANALYTICS ||
            (tab.id === constant.ANALYTICS && baseHelper.isRotaroUser(currentUser) && !currentUser.ecommercePlatform))
      );

      const updatedTabs = tabs
        .map((tab) => {
          if (tab.id === "chat") {
            tab.content = `${tab.content} (${unreadMessageCount})`;
            if (currentUser.ecommercePlatform || isDisabled) {
              return false;
            }
          }
          if (vendorHiddenModule.includes(tab.id)) {
            return false;
          }
          return tab;
        })
        .filter((tab) => tab);

      return updatedTabs;
    }

    return [];
  };

  const currentPath = location.pathname;
  const currentUserRole = storageHelper.get("userRole");
  const tabs = getTabs(currentUserRole);
  const updatedTabs = tabs.map((tab) => {
    return { ...tab, content: tab.content };
  });

  function matchRoute(route, path) {
    // escape regex special chars except ":"
    const regexString = route
      .replace(/:[^/]+/g, "[^/]+") // replace :id with a wildcard
      .replace(/\//g, "\\/"); // escape slashes

    const regex = new RegExp(`^${regexString}$`);
    return regex.test(path);
  }

  const getCurrentTabIndex = () => {
    return tabs.findIndex(
      (tab) => tab.link === currentPath || tab.activeRoutes?.some((route) => matchRoute(route, currentPath))
    );
  };
  const [selectedTab, setSelectedTab] = useState(getCurrentTabIndex());

  useEffect(() => {
    const newIndex = getCurrentTabIndex();
    if (newIndex !== -1 && newIndex !== selectedTab) {
      setSelectedTab(newIndex);
    }
  }, [currentPath, tabs]);

  useEffect(() => {
    const handleTabClick = (event) => {
      const tabButton = event.target.closest('[role="tab"]');

      if (!tabButton) return;

      const tabButtons = tabsRef.current?.querySelectorAll('[role="tab"]');
      
      if (!tabButtons) return;

      const clickedIndex = Array.from(tabButtons).indexOf(tabButton);
      
      if (clickedIndex !== -1) {
        const clickedTab = tabButtons[clickedIndex];
        if (clickedTab.id) {
          const targetTab = tabs.find(item => item.id === clickedTab.id);
          if (targetTab) {
            navigate(targetTab.link);
            setSelectedTab(clickedIndex);
          }
        }
      }
    };

    const tabsContainer = tabsRef.current;
    if (tabsContainer) {
      tabsContainer.addEventListener('click', handleTabClick);
      return () => {
        tabsContainer.removeEventListener('click', handleTabClick);
      };
    }
  }, [tabs, navigate]);

  const handleTabChange = (tabIndex) => {
    navigate(tabs[tabIndex].link);
    setSelectedTab(tabIndex);
  };

  if (isLoading) {
    return null;
  }

  return (
    <StyledTabs ref={tabsRef} className="styled-tabs">
      <PolarisTabs key="main-tab" fitted tabs={updatedTabs} selected={selectedTab} onSelect={handleTabChange} />
    </StyledTabs>
  );
};

export default Tabs;
